/* eslint-disable no-nested-ternary */
import React from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  AddDependency,
  BooleanDependencyForm,
  DependencyContainer,
} from '@app/pages/b2b/QuestionsLibrary/pages/components';
import { AnswerType, QuestionDependencyListView, VisibilityType } from '@data/sdk/match';
import { Namespace } from '@data/utils/enums';
import { AntdTypography } from '@ui/components';
import { QuestionFormValues } from '../../hooks';
import {
  BooleanDependencyPreview,
  ConfirmationModal,
  HiddenQuestionDependencyForm,
  HiddenQuestionDependencyPreview,
} from './components';
import { useDependency } from './hooks/useDependency';

type QuestionDependenciesProps = {
  answerType: AnswerType | undefined;
  dependencies: QuestionDependencyListView | undefined;
  form: FormInstance<QuestionFormValues>;
  handleEditQuestion: (v: QuestionFormValues) => void;
  isFormDirty: boolean;
  questionText: string;
  visibilityType: VisibilityType;
};

const { Text } = AntdTypography;

export const QuestionDependencies = ({
  form,
  answerType,
  isFormDirty,
  dependencies,
  visibilityType,
  questionText,
  handleEditQuestion,
}: QuestionDependenciesProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const isMutating = !!useIsMutating();

  const {
    dependencyRef,
    dependencyToEdit,
    createNewDependency,
    showConfirmationModal,
    dependencyFromSimilarQuestion,
    setDependencyToEdit,
    handleSaveDependency,
    closeDependencyForm,
    setCreateNewDependency,
    toggleConfirmationModal,
    handleDeleteDependency,
  } = useDependency({ questionForm: form, editQuestionMode: isFormDirty, handleEditQuestion });

  return (
    <div className="flex flex-col gap-6 mt-4">
      <div className="flex flex-col gap-3 w-full min-h-[400px]">
        <Text className="font-semibold text-base">
          {t('question_page.common.question_dependencies')}
        </Text>

        <DependencyContainer ref={dependencyRef}>
          {createNewDependency ? (
            visibilityType === VisibilityType.Hidden ? (
              <HiddenQuestionDependencyForm
                onSave={handleSaveDependency}
                questionText={questionText}
                onCancel={closeDependencyForm}
                questionAnswerType={answerType}
                similarQuestionDependency={dependencyFromSimilarQuestion || undefined}
              />
            ) : (
              <BooleanDependencyForm
                onSave={handleSaveDependency}
                answerType={answerType}
                questionText={questionText}
                onCancel={closeDependencyForm}
                similarQuestionDependency={dependencyFromSimilarQuestion || undefined}
              />
            )
          ) : (
            <AddDependency
              onAddDependsOnClick={() => setCreateNewDependency(true)}
              question={questionText}
            />
          )}
        </DependencyContainer>
      </div>

      {dependencies?.sources?.map((dependency) => (
        <DependencyContainer key={dependency.id}>
          {visibilityType === VisibilityType.Hidden ? (
            <>
              {dependencyToEdit?.id === dependency.id ? (
                <HiddenQuestionDependencyForm
                  dependency={dependency}
                  questionText={questionText}
                  onCancel={() => setDependencyToEdit(null)}
                  onSave={handleSaveDependency}
                  onDelete={toggleConfirmationModal}
                  questionAnswerType={answerType}
                />
              ) : (
                <HiddenQuestionDependencyPreview
                  dependency={dependency}
                  questionText={questionText}
                  onEditClick={() => setDependencyToEdit(dependency)}
                />
              )}
            </>
          ) : (
            <>
              {dependencyToEdit?.id === dependency.id ? (
                <BooleanDependencyForm
                  answerType={answerType}
                  questionText={questionText}
                  dependency={dependency}
                  onCancel={() => setDependencyToEdit(null)}
                  onSave={handleSaveDependency}
                  onDelete={toggleConfirmationModal}
                />
              ) : (
                <BooleanDependencyPreview
                  answerType={answerType}
                  onEditClick={() => setDependencyToEdit(dependency)}
                  dependency={dependency}
                  questionText={questionText}
                />
              )}
            </>
          )}
        </DependencyContainer>
      ))}

      <ConfirmationModal
        open={showConfirmationModal}
        title={t('question_page.details.dependencies.delete_dependence')}
        onCancel={toggleConfirmationModal}
        message={
          visibilityType === VisibilityType.Hidden && dependencies?.sources?.length === 1
            ? t('question_page.details.dependencies.delete_last_dependency_text')
            : t('question_page.details.dependencies.delete_dependency_text')
        }
        onOk={handleDeleteDependency}
        isLoading={isMutating}
      />
    </div>
  );
};
